import { useRouter } from 'next/router'
import Button from '@/components/ui/button'
import SidebarNavItem from '@/components/generals/sidebar/sidebar-nav-item'
import SidebarSection from '@/components/generals/sidebar/sidebar-section'
import { ScrollArea } from '@/components/ui/scroll-area'
import { isOpenDeal, isSlipping } from '@/lib/crm'
import { useCrmStore } from '@/stores/crm-store-provider'
import Logo from '@/assets/images/_common/logo.svg'
import BuildingIcon from '@/assets/images/companies/sidebar/building.svg'
import ClipboardIcon from '@/assets/images/companies/sidebar/clipboard.svg'
import BarChartIcon from '@/assets/images/companies/sidebar/bar-chart.svg'
import ListIcon from '@/assets/images/companies/sidebar/list.svg'
import BookClosedIcon from '@/assets/images/companies/sidebar/book-closed.svg'
import MailIcon from '@/assets/images/companies/sidebar/mail.svg'
import TargetIcon from '@/assets/images/companies/sidebar/target-05.svg'
import TargetAltIcon from '@/assets/images/companies/sidebar/target-03.svg'
import UsersIcon from '@/assets/images/companies/sidebar/users.svg'
import BarChartAltIcon from '@/assets/images/companies/sidebar/bar-chart-10.svg'
import AlertTriangleIcon from '@/assets/images/companies/sidebar/alert-triangle.svg'
import DotYellow from '@/assets/images/companies/sidebar/dot-yellow.svg'
import DotPink from '@/assets/images/companies/sidebar/dot-pink.svg'
import DotPurple from '@/assets/images/companies/sidebar/dot-purple.svg'
import UserPlusIcon from '@/assets/images/companies/sidebar/user-plus.svg'
import MessageQuestionIcon from '@/assets/images/companies/sidebar/message-question.svg'
import WalletIcon from '@/assets/images/companies/sidebar/wallet.svg'

export default function SidebarContent() {
    const router = useRouter()
    const path = router.asPath.split(/[?#]/)[0] || '/'
    const companyCount = useCrmStore(state => state.companies.length)
    const openDealCount = useCrmStore(state => state.deals.filter(isOpenDeal).length)
    const openActivityCount = useCrmStore(state => state.activities.filter(activity => !activity.completed).length)
    const contactCount = useCrmStore(state => state.contacts.length)
    const activeSequenceCount = useCrmStore(state => state.sequences.filter(sequence => sequence.status === 'Active').length)
    const slippingCount = useCrmStore(state => state.deals.filter(deal => isSlipping(deal, state.today)).length)
    const northAmericaCount = useCrmStore(state => state.deals.filter(deal => isOpenDeal(deal) && deal.pipeline === 'North America').length)
    const emeaCount = useCrmStore(state => state.deals.filter(deal => isOpenDeal(deal) && deal.pipeline === 'EMEA Enterprise').length)
    const apacCount = useCrmStore(state => state.deals.filter(deal => isOpenDeal(deal) && deal.pipeline === 'APAC Expansion').length)
    const openDialog = useCrmStore(state => state.openDialog)
    const setSidebarOpen = useCrmStore(state => state.setSidebarOpen)

    function isActive(href: string): boolean {
        return path === href
    }

    function invite() {
        setSidebarOpen(false)
        openDialog('invite')
    }

    return (
        <div className='flex h-full min-h-0 flex-col'>
            <div className='border-sidebar-border bg-sidebar-accent flex shrink-0 items-center gap-2 border-b p-3'>
                <Logo aria-hidden className='size-8 shrink-0 overflow-visible' />
                <div className='flex min-w-0 flex-col gap-1'>
                    <span className='lead-style block truncate font-medium tracking-[-0.01em]'>
                        Sales CRM
                    </span>
                    <span className='caption-style text-subtle block truncate'>
                        Company pipeline
                    </span>
                </div>
            </div>

            <ScrollArea className='min-h-0 flex-1'>
                <nav aria-label="Primary">
                    <SidebarSection className='border-sidebar-border border-b'>
                        <SidebarNavItem icon={BuildingIcon} label="Companies" href='/' count={companyCount} active={isActive('/')} />
                        <SidebarNavItem icon={ClipboardIcon} label="Deals Board" href='/deals' count={openDealCount} active={isActive('/deals')} />
                        <SidebarNavItem icon={BarChartIcon} label="Forecast" href='/forecast' active={isActive('/forecast')} />
                        <SidebarNavItem icon={ListIcon} label="Activities" href='/activities' count={openActivityCount} active={isActive('/activities')} />
                        <SidebarNavItem icon={BookClosedIcon} label="Contacts" href='/contacts' count={contactCount} active={isActive('/contacts')} />
                        <SidebarNavItem icon={MailIcon} label="Email Sequences" href='/sequences' count={activeSequenceCount} active={isActive('/sequences')} />
                    </SidebarSection>

                    <SidebarSection title="Team" className='border-sidebar-border border-b'>
                        <SidebarNavItem icon={TargetIcon} label="Strategic AEs" href='/team/strategic-aes' active={isActive('/team/strategic-aes')} />
                        <SidebarNavItem icon={TargetAltIcon} label="Mid Market" href='/team/mid-market' active={isActive('/team/mid-market')} />
                        <SidebarNavItem icon={UsersIcon} label="SDR Team" href='/team/sdr-team' active={isActive('/team/sdr-team')} />
                    </SidebarSection>

                    <SidebarSection title="Reporting" className='border-sidebar-border border-b'>
                        <SidebarNavItem icon={BarChartAltIcon} label="Q1 Forecast" href='/reports/q1-forecast' active={isActive('/reports/q1-forecast')} />
                        <SidebarNavItem icon={AlertTriangleIcon} label="Slipping Deals" href='/reports/slipping-deals' count={slippingCount} active={isActive('/reports/slipping-deals')} />
                    </SidebarSection>

                    <SidebarSection title="Pipelines">
                        <SidebarNavItem icon={DotYellow} label="North America" href='/pipelines/north-america' count={northAmericaCount} active={isActive('/pipelines/north-america')} />
                        <SidebarNavItem icon={DotPink} label="EMEA Enterprise" href='/pipelines/emea-enterprise' count={emeaCount} active={isActive('/pipelines/emea-enterprise')} />
                        <SidebarNavItem icon={DotPurple} label="APAC Expansion" href='/pipelines/apac-expansion' count={apacCount} active={isActive('/pipelines/apac-expansion')} />
                    </SidebarSection>
                </nav>
            </ScrollArea>

            <SidebarSection className='border-sidebar-border shrink-0 border-t border-b'>
                <SidebarNavItem icon={UserPlusIcon} label="Invite teammates" tone='quiet' onClick={invite} />
                <SidebarNavItem icon={MessageQuestionIcon} label="Help" tone='quiet' href='/help' active={isActive('/help')} />
            </SidebarSection>

            <div className='border-sidebar-border bg-sidebar-accent flex shrink-0 items-center justify-between gap-2 border-b p-4'>
                <div className='flex flex-col gap-2'>
                    <span className='lead-style block font-medium tracking-[-0.01em]'>
                        14 Days
                    </span>
                    <span className='caption-style text-subtle block'>
                        Left on trials
                    </span>
                </div>
                <Button variant='muted' size='md' href='/help?topic=billing'>
                    <WalletIcon aria-hidden className='size-3.5' />
                    Add Billings
                </Button>
            </div>
        </div>
    )
}
